/* eslint-disable */
import { SecureRoute } from '@okta/okta-react';
import { Menu } from 'antd';
import { TaskId } from 'api/models';
import { LinkToTask } from 'components/atoms/link-to-task';
import { Page } from 'components/molecules/page/page';
import React, { FC } from 'react';
import { Link, Switch, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { lazyWithRetry } from 'utils/lazyWithRetry';
import { DemoAlertPage } from './alert';
import { DemoAttachmentPage } from './attachments';
import { BannerDemoPage } from './banner';
import { DemoCardLinkPage } from './card-link';
import { ContainedListDemo } from './contained-list';

import './demos.css';
import { DrawerDemo } from './drawer';
import { EmptyStateDemo } from './empty-state';
import { FilterDrawerDemo } from './filter-drawer';
import { DemoImageUploadPage } from './image-upload';
import { DemoInputPage } from './input';
import { MaskableContentDemo } from './maskable-content';
import { Notifications } from './notifications';
import { DemoPopconfirmPage } from './popconfirm';
import { DemoProgressBarPage } from './progress-bar';
import { DemoRangeWithRelativePicker } from './range-with-relative-picker';
import { SwitcherDemo } from './switcher';
import { DemoTableSkeleton } from './table-skeleton';
import { DemoToast } from './toast';
import { DemoUserInvitations } from './user-invitations';

const Content = styled.div`
  display: flex;
  padding: 0 var(--layout-01);
  overflow: hidden;
  height: calc(100vh - 76px);
`;

const Sider = styled.div`
  display: flex;
  flex: 1 1 300px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 var(--layout-01);
  border-right: 1px solid var(--border-color);
`;

const Demo = styled.div`
  display: flex;
  flex: 1 1 100%;
  flex-direction: column;
  overflow: auto;
  padding: var(--layout-01);
`;

const TEXT = {
  h1: 'Header ',
  h7: 'Header 7',
  openTaskPane: 'Open task pane',
  loadingHeader: 'Loading states',
  avatarHeader: 'Avatars',
};

const LazyAvatar = lazyWithRetry(() =>
  import('routes/demos/avatar').then(({ DemoAvatar }) => ({
    default: DemoAvatar,
  })),
);

const LazyTypography = lazyWithRetry(() =>
  import('routes/demos/typography').then(({ DemoTypography }) => ({
    default: DemoTypography,
  })),
);

const LazyLoading = lazyWithRetry(() =>
  import('routes/demos/loading').then(({ DemoLoadingPage }) => ({
    default: DemoLoadingPage,
  })),
);

const LazyRTEWI = lazyWithRetry(() =>
  import('routes/demos/rich-text-editor-demo').then(
    ({ RichTextEditorDemoWithImages }) => ({
      default: RichTextEditorDemoWithImages,
    }),
  ),
);

const LazyDocEditor = lazyWithRetry(() =>
  import('routes/demos/doc-editor/doc-editor').then(({ DocEditor }) => ({
    default: DocEditor,
  })),
);

const LazyRTEWOI = lazyWithRetry(() =>
  import('routes/demos/rich-text-editor-demo').then(
    ({ RichTextEditorDemoWithoutImages }) => ({
      default: RichTextEditorDemoWithoutImages,
    }),
  ),
);

const LazyDueDatePicker = lazyWithRetry(() =>
  import('routes/demos/due-date-picker').then(({ DemoForDueDatePicker }) => ({
    default: DemoForDueDatePicker,
  })),
);

const LazyModal = lazyWithRetry(() =>
  import('routes/demos/modal').then(({ DemoModalPage }) => ({
    default: DemoModalPage,
  })),
);

const LazyForm = lazyWithRetry(() =>
  import('routes/demos/form').then(({ DemoFormPage }) => ({
    default: DemoFormPage,
  })),
);

const LazySelect = lazyWithRetry(() =>
  import('routes/demos/select').then(({ DemoSelectPage }) => ({
    default: DemoSelectPage,
  })),
);

const LazyNewRow = lazyWithRetry(() =>
  import('routes/demos/new-row').then(({ DemoNewRow }) => ({
    default: DemoNewRow,
  })),
);

const LazyStatusPicker = lazyWithRetry(() =>
  import('routes/demos/status-picker').then(({ DemoStatusPicker }) => ({
    default: DemoStatusPicker,
  })),
);

const LazyButton = lazyWithRetry(() =>
  import('routes/demos/button').then(({ ButtonDemo }) => ({
    default: ButtonDemo,
  })),
);

const LazySquadPicker = lazyWithRetry(() =>
  import('routes/demos/squad-picker').then(({ SquadPickerDemo }) => ({
    default: SquadPickerDemo,
  })),
);

const LazyUserInvitation = lazyWithRetry(() =>
  import('routes/demos/user-invitations').then(({ DemoUserInvitations }) => ({
    default: DemoUserInvitations,
  })),
);

export const DemoPage: FC = () => {
  const { path } = useRouteMatch();

  return (
    <Page>
      <Page.Header title={<Page.Header.Title>{TEXT.h1}</Page.Header.Title>} />
      <Content>
        <Sider>
          <Menu>
            <Menu.Item>
              <Link to="/demos/banner">Banner</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/typography">Typography</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/rte">RTE Demo</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/doc-editor">Document Editor</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/due-date-picker">Due date picker</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/avatar">Avatar</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/loading">Loading</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/modal">Modal</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/form">Form</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/select">Select</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/button">Button</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/datatable">Datatable</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/new-row">New Row</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/status-picker">Status picker</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/alert">Alert</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/attachments">Attachments</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/progress-bar">ProgressBar</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/popconfirm">Popconfirm</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/range-with-relative-time">
                Range dropdown with relative days
              </Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/toast">Toast service</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/empty-state">Empty State</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/table-skeleton">Table Skeleton</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/notifications">Notifications</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/squad-picker">
                Squad Picker AKA Assignee Dropdown)
              </Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/user-invitation">User Invitations</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/switcher">Switcher</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/contained-list">Contained list</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/image-upload">Image Uploader</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/card-link">Card Link</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/drawer">Drawer</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/filter-drawer">Filter drawer</Link>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/input">Input</Link>
            </Menu.Item>
            <Menu.Item>
              <LinkToTask id={TaskId(101, 845)}>{TEXT.openTaskPane}</LinkToTask>
            </Menu.Item>
            <Menu.Item>
              <Link to="/demos/maskable-content">Maskable Content</Link>
            </Menu.Item>
          </Menu>
        </Sider>
        <Demo>
          <Switch>
            <SecureRoute path={`${path}/rte`} component={LazyRTEWI} />
            <SecureRoute
              path={`${path}/rte/no-images`}
              component={LazyRTEWOI}
            />
            <SecureRoute
              path={`${path}/due-date-picker`}
              component={LazyDueDatePicker}
            />
            <SecureRoute path={`${path}/loading`} component={LazyLoading} />
            <SecureRoute path={`${path}/avatar`} component={LazyAvatar} />
            <SecureRoute path={`${path}/modal`} component={LazyModal} />
            <SecureRoute path={`${path}/form`} component={LazyForm} />
            <SecureRoute path={`${path}/select`} component={LazySelect} />
            <SecureRoute path={`${path}/button`} component={LazyButton} />
            <SecureRoute path={`${path}/new-row`} component={LazyNewRow} />
            <SecureRoute
              path={`${path}/squad-picker`}
              component={LazySquadPicker}
            />
            <SecureRoute
              path={`${path}/status-picker`}
              component={LazyStatusPicker}
            />
            <SecureRoute path={`${path}/alert`} component={DemoAlertPage} />
            <SecureRoute
              path={`${path}/attachments`}
              component={DemoAttachmentPage}
            />

            <SecureRoute
              path={`${path}/progress-bar`}
              component={DemoProgressBarPage}
            />
            <SecureRoute
              path={`${path}/popconfirm`}
              component={DemoPopconfirmPage}
            />
            <SecureRoute
              path={`${path}/range-with-relative-time`}
              component={DemoRangeWithRelativePicker}
            />
            <SecureRoute path={`${path}/toast`} component={DemoToast} />
            <SecureRoute
              path={`${path}/empty-state`}
              component={EmptyStateDemo}
            />
            <SecureRoute
              path={`${path}/filter-drawer`}
              component={FilterDrawerDemo}
            />
            <SecureRoute
              path={`${path}/table-skeleton`}
              component={DemoTableSkeleton}
            />
            <SecureRoute
              path={`${path}/notifications`}
              component={Notifications}
            />
            <SecureRoute
              path={`${path}/user-invitation`}
              component={DemoUserInvitations}
            />
            <SecureRoute path={`${path}/drawer`} component={DrawerDemo} />
            <SecureRoute
              path={`${path}/maskable-content`}
              component={MaskableContentDemo}
            />

            <SecureRoute path={`${path}/switcher`} component={SwitcherDemo} />
            <SecureRoute
              path={`${path}/contained-list`}
              component={ContainedListDemo}
            />
            <SecureRoute
              path={`${path}/image-upload`}
              component={DemoImageUploadPage}
            />
            <SecureRoute
              path={`${path}/card-link`}
              component={DemoCardLinkPage}
            />
            <SecureRoute
              path={`${path}/doc-editor`}
              component={LazyDocEditor}
            />
            <SecureRoute path={`${path}/input`} component={DemoInputPage} />
            <SecureRoute path={`${path}/banner`} component={BannerDemoPage} />
            <SecureRoute path={path} component={LazyTypography} />
          </Switch>
        </Demo>
      </Content>
    </Page>
  );
};
